/**
 * PluckPad — Karplus-Strong plucked-synth pad.
 *
 * `Tone.PluckSynth` chained into a ping-pong delay + reverb send
 * for a spacious, slowly-evolving pluck tone. Good for airy
 * arpeggios, sparse melodic parts, or held-chord drones where
 * the pluck transient defines the rhythm and the tails blend
 * into a pad.
 */

import { PluckSynth, PingPongDelay, Reverb, Gain as ToneGain } from 'tone'
import { registerInstrument } from '@/audio/plugins/registry'
import type {
  InstrumentPlugin,
  InstrumentVoiceHandle,
} from '@/audio/plugins/types'
import { registerToneContext } from '@/audio/tone/registerContext'

let registered = false

export function registerPluckPadInstrument(): void {
  if (registered) return
  registered = true
  registerInstrument(pluckPadInstrument)
}

const pluckPadInstrument: InstrumentPlugin = {
  id: 'pluck-pad',
  label: 'Pluck Pad',
  category: 'synth',
  defaultPreset: {
    pitch: 60,
    attackNoise: 0.5,
    dampening: 4000,
    resonance: 0.9,
    delayTime: 0.375,
    delayFeedback: 0.35,
    reverbDecay: 4,
    reverbWet: 0.4,
  },
  paramSchema: [
    {
      id: 'pitch',
      label: 'Key',
      kind: 'slider',
      min: 24,
      max: 96,
      step: 1,
      default: 60,
      format: 'midi-note',
      help: 'Base note the plucked string plays at. Per-step pitch locks add a semitone offset.',
    },
    {
      id: 'attackNoise',
      label: 'Attack Noise',
      kind: 'slider',
      min: 0.1,
      max: 20,
      step: 0.1,
      default: 0.5,
    },
    {
      id: 'dampening',
      label: 'Dampening',
      kind: 'freq',
      min: 500,
      max: 12000,
      step: 50,
      default: 4000,
    },
    {
      id: 'resonance',
      label: 'Resonance',
      kind: 'slider',
      min: 0,
      max: 0.99,
      step: 0.01,
      default: 0.9,
    },
    {
      id: 'delayTime',
      label: 'Delay (s)',
      kind: 'time',
      min: 0.01,
      max: 1,
      step: 0.005,
      default: 0.375,
    },
    {
      id: 'delayFeedback',
      label: 'Delay Fdbk',
      kind: 'slider',
      min: 0,
      max: 0.9,
      step: 0.01,
      default: 0.35,
    },
    {
      id: 'reverbDecay',
      label: 'Reverb Decay',
      kind: 'time',
      min: 0.5,
      max: 15,
      step: 0.1,
      default: 4,
    },
    {
      id: 'reverbWet',
      label: 'Reverb Wet',
      kind: 'slider',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.4,
    },
  ],
  createVoice: (ctx, preset = {}) => createPluckPadVoice(ctx, preset),
}

function createPluckPadVoice(
  ctx: AudioContext,
  preset: Record<string, unknown>,
): InstrumentVoiceHandle {
  registerToneContext(ctx)

  const p = preset as Record<string, number>
  const basePitch = typeof p.pitch === 'number' ? p.pitch : 60

  const pluck = new PluckSynth({
    attackNoise: typeof p.attackNoise === 'number' ? p.attackNoise : 0.5,
    dampening: typeof p.dampening === 'number' ? p.dampening : 4000,
    resonance: typeof p.resonance === 'number' ? p.resonance : 0.9,
  })
  const delay = new PingPongDelay({
    delayTime: typeof p.delayTime === 'number' ? p.delayTime : 0.375,
    feedback: typeof p.delayFeedback === 'number' ? p.delayFeedback : 0.35,
    wet: 0.5,
  })
  const reverb = new Reverb({
    decay: typeof p.reverbDecay === 'number' ? p.reverbDecay : 4,
    wet: typeof p.reverbWet === 'number' ? p.reverbWet : 0.4,
  })
  // Reverb.ready is ignored intentionally — the IR takes a few ms
  // to generate, and voices fired before it's ready pass through
  // dry until it arrives. Matches the hand-rolled reverb's behaviour.

  const output = new ToneGain({ gain: 1 })
  pluck.connect(delay).connect(reverb).connect(output)

  let disposed = false

  return {
    output: output.output as unknown as AudioNode,
    trigger(when, velocity, pitchSemitones) {
      if (disposed) return
      const midi = basePitch + pitchSemitones
      const freq = 440 * Math.pow(2, (midi - 69) / 12)
      const startT = Math.max(when, ctx.currentTime)
      // PluckSynth's triggerAttack ignores the velocity param
      // directly; scale output gain briefly to apply it.
      output.gain.cancelScheduledValues(startT)
      output.gain.setValueAtTime(Math.max(0, Math.min(1, velocity)), startT)
      pluck.triggerAttack(freq, startT)
    },
    release(when) {
      if (disposed) return
      // PluckSynth tails naturally — no explicit release beyond
      // what dampening + resonance already define. Fade the
      // output gain to avoid lingering tail across re-triggers.
      const t = Math.max(when, ctx.currentTime)
      output.gain.cancelScheduledValues(t)
      output.gain.setValueAtTime(output.gain.value, t)
      output.gain.linearRampToValueAtTime(0, t + 0.05)
    },
    dispose() {
      if (disposed) return
      disposed = true
      try {
        pluck.dispose()
      } catch {
        /* already */
      }
      try {
        delay.dispose()
      } catch {
        /* already */
      }
      try {
        reverb.dispose()
      } catch {
        /* already */
      }
      try {
        output.dispose()
      } catch {
        /* already */
      }
    },
  }
}
